// src/pages/SignupInvite.js
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import '../Styles/App.css';
import styles from '../Styles/App.module.css';
import logo from '../Images/logooranssi.png';

import { verifyInvite, redeemInvite } from '../Api/UsersApi';

export default function SignupInvite() {
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get('token');

  const [invite, setInvite]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [fatalError, setFatalError] = useState('');   // vain fataaleille
  const [formError, setFormError]   = useState('');   // inline-lomakevirhe

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [pw, setPw]               = useState('');
  const [pw2, setPw2]             = useState('');
  const [busy, setBusy]           = useState(false);

  const firstNameRef = useRef(null);
  const lastNameRef  = useRef(null);
  const pwRef        = useRef(null);
  const pw2Ref       = useRef(null);

  useEffect(() => {
    if (!token) { setFatalError('Puuttuva token.'); setLoading(false); return; }
    (async () => {
      try {
        const data = await verifyInvite(token);
        setInvite(data);
        if (data?.name) {
          const parts = String(data.name).trim().split(' ');
          setFirstName(parts[0] || '');
          setLastName(parts.slice(1).join(' ') || '');
        }
      } catch (e) {
        setFatalError(e.code || 'Virhe kutsun tarkistuksessa.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!loading && !fatalError) {
      setTimeout(() => firstNameRef.current?.focus(), 0);
    }
  }, [loading, fatalError]);

  const focusFirstError = () => {
    if (!firstName.trim()) { firstNameRef.current?.focus(); return; }
    if (!lastName.trim())  { lastNameRef.current?.focus();  return; }
    if (!pw || pw.length < 6) { pwRef.current?.focus(); return; }
    if (pw !== pw2) { pw2Ref.current?.focus(); return; }
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setFormError('');

    // Inline-validointi
    if (!firstName.trim() || !lastName.trim()) {
      setFormError('Etunimi ja sukunimi ovat pakollisia.');
      focusFirstError();
      return;
    }
    if (!pw || pw.length < 6) {
      setFormError('Salasanan tulee olla vähintään 6 merkkiä.');
      focusFirstError();
      return;
    }
    if (pw !== pw2) {
      setFormError('Salasanat eivät täsmää.');
      focusFirstError();
      return;
    }

    setBusy(true);
    try {
      await redeemInvite({
        token,
        password: pw,
        firstName: firstName.trim(),
        lastName: lastName.trim()
      });
      navigate('/login');
    } catch (e) {
      // nämä ovat fataaleja palvelinvirheitä — näytetään erillään
      if (e.code === 'expired') setFatalError('Kutsu on vanhentunut.');
      else if (e.code === 'invalid_status') setFatalError('Kutsua ei voi lunastaa.');
      else if (e.code === 'email_in_use') setFatalError('Sähköposti on jo käytössä.');
      else setFatalError('Lunastus epäonnistui. Yritä uudelleen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App" style={{ backgroundColor: "#282828", position: "relative", minHeight: "100vh", overflow: "hidden" }}>
      {/* Vesileima */}
      <div
        style={{
          position: "fixed", inset: 0,
          backgroundImage: `url(${logo})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "clamp(320px, 45vw, 820px)",
          opacity: 0.06, pointerEvents: "none", zIndex: 0,
        }}
      />
      {/* Brändi vasempaan yläkulmaan */}
      <div className={styles.brandCorner}>
        <div className={styles.logoRow}>
          <img src="logooranssi.png" alt="AsukasHuudi logo" className={styles.brandLogo}/>
          <div className={styles.brandName}>AsukasHuudi</div>
        </div>
        <div className={styles.slogan}>"Talonyhtiösi digitaalinen koti"</div>
      </div>
      {/* Kortti keskelle */}
      <div className="login-container signup-container">
        <h1>Viimeistele rekisteröinti</h1>
        {loading && <p className="login-reset-message">Tarkistetaan kutsua…</p>}
        {/* Fataali virhe (esim. token ongelma) */}
        {!loading && fatalError && (
          <div className="login-error" role="alert" style={{ marginBottom: 8 }}>{fatalError}</div>
        )}
        {/* Lomake näkyy aina kun ei ole fataalia virhettä */}
        {!loading && !fatalError && invite && (
          <form onSubmit={onSubmit}>
            {/* Sähköposti infoboksi */}
            <div style={{
              background:'#1f1f1f',
              border:'1px solid #2e2e2e',
              borderRadius:8,
              padding:'10px 12px',
              color:'#e6e6e6',
              fontSize:14,
              marginBottom:12
            }}>
              <div><strong>Sähköposti:</strong> {invite.email}</div>
            </div>
            {/* Etunimi + sukunimi vierekkäin */}
            <div className={styles.formRowGrid}>
              <div className={styles.formCol}>
                <label htmlFor="firstName">Etunimi</label>
                <input
                  id="firstName"
                  type="text"
                  placeholder="Etunimi"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  ref={firstNameRef}
                />
              </div>
              <div className={styles.formCol}>
                <label htmlFor="lastName">Sukunimi</label>
                <input
                  id="lastName"
                  type="text"
                  placeholder="Sukunimi"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  ref={lastNameRef}
                />
              </div>
            </div>
            <label htmlFor="password">Salasana</label>
            <input
              id="password"
              type="password"
              placeholder="Anna vähintään 6 merkkiä"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              ref={pwRef}
            />
            <label htmlFor="password2">Salasana uudelleen</label>
            <input
              id="password2"
              type="password"
              placeholder="Toista salasana"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              ref={pw2Ref}
            />
            {/* Inline-lomakevirhe — lomake pysyy näkyvissä */}
            {formError && <div className="login-error" role="alert" style={{ marginTop: -6, marginBottom: 6 }}>{formError}</div>}
            <div className={styles.buttonContainer}>
              <button type="submit" disabled={busy}>
                {busy ? 'Luodaan tili…' : 'Luo tili'}
              </button>
            </div>
            <div style={{ textAlign:'center', marginTop: 6 }}>
              <span className={styles.resetLink} onClick={() => navigate('/login')}>
                &lt; Takaisin kirjautumiseen
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
